import ApiClient from "@/services/api/apiClient";
import { API_ENDPOINTS } from "@/services/api/endpoints";

/** One entry of the board / class dropdowns of the add-student form. */
export type LookupOption = {
  value: string;
  /** The plain name (what the request sends and what saved values are matched against). */
  label: string;
  /** Shown after the name as "name (description)" in the dropdown (boards only). */
  description?: string;
};

/** One entry of the section dropdown; carries the section's academic year when it has one. */
export type SectionOption = LookupOption & { academicYear?: string };

/*
 * Response shapes (confirmed by the product owner unless marked "tolerant"):
 *
 *   boards    GET api/v1/metadata/boards                       (existing useBoards contract)
 *             { success, data: [ { id, name } ] }
 *   classes   GET api/v1/helpers/get-board-classes?board_id={id}
 *             { classes: [ { id, name } ] }                     `id` IS the board_class_id
 *   sections  GET api/v1/helpers/get-class-sections?board_class_id={id}&institute_id={id}
 *             { sections: [ { id, section_label, academic_year, class_teacher_id } ] }
 *
 * Tolerant fallbacks (not confirmed, kept so a small backend change does not break the
 * form): rows may sit under `data` | `items` | `results`, in a bare array or one level
 * deeper in a `{ success, data }` envelope; a class name may come from `class_name` |
 * `class` | `label`; a section label from `section_name` | `name` | `section` | `label`.
 * Rows without an id or without a name are skipped and the API order is preserved.
 */

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function pickString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/** First non-empty string among `keys` of `item`. */
function pickFirst(item: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const value = pickString(item[key]);
    if (value) return value;
  }
  return "";
}

/** Finds the rows in a bare array, `{ [featureKey] | data | items | results: [] }` or a `{ success, data }` envelope. */
function extractItems(body: unknown, featureKey: string, depth = 0): unknown[] {
  if (Array.isArray(body)) return body;
  const rec = asRecord(body);
  if (!rec || depth > 2) return [];
  for (const key of [featureKey, "data", "items", "results"]) {
    if (Array.isArray(rec[key])) return rec[key] as unknown[];
  }
  for (const key of ["data", "result"]) {
    const nested = extractItems(rec[key], featureKey, depth + 1);
    if (nested.length > 0) return nested;
  }
  return [];
}

/** Throws on an explicit `{ success: false }` body, using its message when there is one. */
function assertNotFailed(body: unknown, fallbackMessage: string): void {
  const rec = asRecord(body);
  if (rec?.success === false) {
    const message = rec.message;
    throw new Error(
      typeof message === "string" && message ? message : fallbackMessage,
    );
  }
}

function mapNamedOptions(
  body: unknown,
  featureKey: string,
  nameKeys: readonly string[],
  descriptionKey?: string,
): LookupOption[] {
  const options: LookupOption[] = [];
  for (const raw of extractItems(body, featureKey)) {
    const item = asRecord(raw);
    if (!item) continue;
    const id = pickString(item.id);
    const label = pickFirst(item, nameKeys);
    if (!id || !label) continue;
    const description = descriptionKey ? pickString(item[descriptionKey]) : "";
    options.push(description ? { value: id, label, description } : { value: id, label });
  }
  return options;
}

// ---------------------------------------------------------------- boards

/**
 * Boards body (`useBoards` data: `{ success, data: [ { id, name } ] }`, or a bare array)
 * -> options `{ value: String(id), label: name, description? }`.
 */
export function mapBoardOptions(body: unknown): LookupOption[] {
  return mapNamedOptions(body, "boards", ["name"], "description");
}

// ---------------------------------------------------------------- classes

/** URL of the classes lookup. Query param: `board_id`. */
export const BOARD_CLASSES_ENDPOINT = API_ENDPOINTS.HELPERS.BOARD_CLASSES;

/** First whole number in a class name ("Class 10" -> 10, "2" -> 2), or null when it has none. */
function classNumber(label: string): number | null {
  const match = /\d+/.exec(label);
  return match ? Number(match[0]) : null;
}

/**
 * Orders classes numerically ("1, 2, ... 9, 10, 11, 12"; the API returns them as text order
 * "1, 10, 11, 12, 2, ..."). Names without a number (for example "Nursery" or "LKG") keep the
 * API's relative order and are listed first; equal numbers fall back to a natural text compare.
 */
function sortClassOptions(options: LookupOption[]): LookupOption[] {
  const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });
  const indexed = options.map((option, index) => ({
    option,
    index,
    number: classNumber(option.label),
  }));
  indexed.sort((a, b) => {
    if (a.number === null && b.number === null) return a.index - b.index;
    if (a.number === null) return -1;
    if (b.number === null) return 1;
    return (
      a.number - b.number ||
      collator.compare(a.option.label, b.option.label) ||
      a.index - b.index
    );
  });
  return indexed.map((entry) => entry.option);
}

/**
 * `{ classes: [ { id, name } ] }` -> options sorted by class number; the option value is the
 * board_class_id.
 */
export function mapClassOptions(body: unknown): LookupOption[] {
  return sortClassOptions(
    mapNamedOptions(body, "classes", ["name", "class_name", "class", "label"]),
  );
}

/** Classes of one board. */
export async function fetchClassOptions(boardId: string): Promise<LookupOption[]> {
  const body: unknown = await ApiClient.get(BOARD_CLASSES_ENDPOINT, {
    board_id: boardId,
  });
  assertNotFailed(body, "Failed to load classes");
  return mapClassOptions(body);
}

// ---------------------------------------------------------------- sections

/** URL of the sections lookup. Query params: `board_class_id`, `institute_id`. */
export const CLASS_SECTIONS_ENDPOINT = API_ENDPOINTS.HELPERS.CLASS_SECTIONS;

/**
 * `{ sections: [ { id, section_label, academic_year, class_teacher_id } ] }` -> options
 * `{ value: String(id), label: section_label, academicYear? }`. `academicYear` is the
 * trimmed `academic_year` and is omitted when empty or missing; `class_teacher_id` is ignored.
 */
export function mapSectionOptions(body: unknown): SectionOption[] {
  const options: SectionOption[] = [];
  for (const raw of extractItems(body, "sections")) {
    const item = asRecord(raw);
    if (!item) continue;
    const id = pickString(item.id);
    const label = pickFirst(item, [
      "section_label",
      "section_name",
      "name",
      "section",
      "label",
    ]);
    if (!id || !label) continue;
    const academicYear = pickString(item.academic_year);
    options.push(
      academicYear ? { value: id, label, academicYear } : { value: id, label },
    );
  }
  return options;
}

// ---------------------------------------------------------------- streams

/** URL of the streams lookup. Query params: `institute_id`, `board_id`, `class_id`. */
export const STREAMS_ENDPOINT = API_ENDPOINTS.BOARDS.STREAMS;

/**
 * `{ streams: [ { id, name, board_id, class_id, institute_id, status } ] }` -> options
 * `{ value: String(id), label: name }`. Only `id` and `name` are used; the API order is kept.
 */
export function mapStreamOptions(body: unknown): LookupOption[] {
  return mapNamedOptions(body, "streams", ["name"]);
}

/** Streams of one institute + board + class (the ids of the form's three dropdowns). */
export async function fetchStreamOptions(ids: {
  instituteId: string;
  boardId: string;
  classId: string;
}): Promise<LookupOption[]> {
  const body: unknown = await ApiClient.get(STREAMS_ENDPOINT, {
    institute_id: ids.instituteId,
    board_id: ids.boardId,
    class_id: ids.classId,
  });
  assertNotFailed(body, "Failed to load streams");
  return mapStreamOptions(body);
}

/**
 * Sections of one class at one institute (`classId` is the id returned by the classes
 * lookup, `instituteId` the id of the form's institute dropdown).
 */
export async function fetchSectionOptions(ids: {
  instituteId: string;
  classId: string;
}): Promise<SectionOption[]> {
  const body: unknown = await ApiClient.get(CLASS_SECTIONS_ENDPOINT, {
    board_class_id: ids.classId,
    institute_id: ids.instituteId,
  });
  assertNotFailed(body, "Failed to load sections");
  return mapSectionOptions(body);
}
